import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Checkbox,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Compare as CompareIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  Article as ArticleIcon,
  QuestionAnswer as QuestionAnswerIcon,
  PhotoCamera as PhotoCameraIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  QuestionAnswer as QAIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon
} from '@mui/icons-material';
import api from '../services/api';
import LazyDataViewer from '../components/LazyDataViewer';

const ProfileDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSnapshots, setSelectedSnapshots] = useState(new Set());
  const [capturingSnapshot, setCapturingSnapshot] = useState(false);
  const [snapshotDetailsOpen, setSnapshotDetailsOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);

  const fetchProfileAndSnapshots = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch profile details
      const profileResponse = await api.get(`/api/profiles/${id}`);
      setProfile(profileResponse.data);

      // Fetch snapshots for this profile (backward compatible)
      const snapshotsResponse = await api.get(`/api/snapshots/profile/${id}`);
      setSnapshots(snapshotsResponse.data);
    } catch (error) {
      console.error('Error fetching profile data:', error);
      setError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProfileAndSnapshots();
  }, [fetchProfileAndSnapshots]);

  const handleSnapshotSelect = (snapshotId) => {
    const newSelected = new Set(selectedSnapshots);
    if (newSelected.has(snapshotId)) {
      newSelected.delete(snapshotId);
    } else {
      newSelected.add(snapshotId);
    }
    setSelectedSnapshots(newSelected);
  };

  const handleCaptureSnapshot = async () => {
    try {
      setCapturingSnapshot(true);
      setError(null);
      
      const response = await api.post('/api/snapshots/capture', {
        profileId: id
      });
      
      if (response.data.success) {
        // Refresh snapshots after capture
        await fetchProfileAndSnapshots();
        setError(null);
      } else {
        setError('Failed to capture snapshot');
      }
    } catch (error) {
      console.error('Error capturing snapshot:', error);
      setError('Failed to capture snapshot');
    } finally {
      setCapturingSnapshot(false);
    }
  };

  const handleCompareSnapshots = async () => {
    if (selectedSnapshots.size !== 2) {
      setError('Please select exactly 2 snapshots to compare');
      return;
    }

    const snapshotIds = Array.from(selectedSnapshots);
    navigate(`/compare?snapshot1=${snapshotIds[0]}&snapshot2=${snapshotIds[1]}`);
  };

  const handleViewSnapshotDetails = (snapshot) => {
    setSelectedSnapshot(snapshot);
    setSnapshotDetailsOpen(true);
    setSelectedTab(0);
    
    // Debug: Log the snapshot data structure
    console.log('🔍 Selected Snapshot Data:', snapshot);
    console.log('🔍 Raw Data Keys:', Object.keys(snapshot.rawData || {}));
    console.log('🔍 Listing Data:', snapshot.rawData?.listing);
    console.log('🔍 Listing Data Keys:', snapshot.rawData?.listing ? Object.keys(snapshot.rawData.listing) : 'None');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          Profile not found
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2, color: 'text.secondary' }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography 
              variant="h3" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                color: '#ffffff',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
                mb: 1
              }}
              gutterBottom
            >
              {profile.name}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}
            >
              Google Business Profile Details
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Profile Information Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid xs={12} md={6}>
          <Card className="modern-card">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon color="primary" />
                Business Information
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Address:</strong> {profile.address || 'No address'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Phone:</strong> {profile.phone || 'No phone'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Website:</strong> {profile.website || 'No website'}
                  </Typography>
                </Grid>
                <Grid xs={12} md={6}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Primary Category:</strong> {profile.category || 'Uncategorized'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>GBP ID:</strong> {profile.gbpId}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  {profile.businessStatus && (
                    <Grid xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Status:</strong> {profile.businessStatus}
                      </Typography>
                    </Grid>
                  )}
                  {profile.priceLevel && (
                    <Grid xs={6}>
                      <Typography variant="body2" color="textSecondary">
                        <strong>Price Level:</strong> {profile.priceLevel.replace('PRICE_LEVEL_', '')}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid xs={12} md={6}>
          <Card className="modern-card">
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TimelineIcon color="primary" />
                Snapshot Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid xs={6}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                    {snapshots.length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Snapshots
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                    {snapshots.filter(s => s.rawData?.reviews?.data?.reviews).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    With Reviews
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                    {snapshots.filter(s => s.rawData?.posts?.data?.localPosts).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    With Posts
                  </Typography>
                </Grid>
                <Grid xs={6}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                    {snapshots.filter(s => s.rawData?.qa?.data?.questions).length}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    With Q&A
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Snapshots Table */}
      <Card className="modern-card">
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              Snapshots ({snapshots.length})
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                startIcon={capturingSnapshot ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                onClick={handleCaptureSnapshot}
                disabled={capturingSnapshot}
                size="small"
                className="modern-button"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              >
                {capturingSnapshot ? 'Capturing...' : 'Capture New Snapshot'}
              </Button>
              
              {selectedSnapshots.size === 2 && (
                <Button
                  variant="outlined"
                  startIcon={<CompareIcon />}
                  onClick={handleCompareSnapshots}
                  size="small"
                  className="modern-button"
                >
                  Compare Selected ({selectedSnapshots.size})
                </Button>
              )}
            </Box>
          </Box>
          
          {snapshots.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <TimelineIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="textSecondary" gutterBottom>
                No snapshots available
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Capture your first snapshot to get started
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedSnapshots.size > 0 && selectedSnapshots.size < snapshots.length}
                        checked={selectedSnapshots.size === snapshots.length && snapshots.length > 0}
                        onChange={() => {
                          if (selectedSnapshots.size === snapshots.length) {
                            setSelectedSnapshots(new Set());
                          } else {
                            setSelectedSnapshots(new Set(snapshots.map(s => s._id)));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Data Overview</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {snapshots.map((snapshot) => (
                    <TableRow key={snapshot._id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedSnapshots.has(snapshot._id)}
                          onChange={() => handleSnapshotSelect(snapshot._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1a202c' }}>
                          {formatDate(snapshot.captureDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <StarIcon color={snapshot.rawData?.reviews?.data?.reviews ? "success" : "error"} fontSize="small" />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                              {snapshot.rawData?.reviews?.data?.reviews ? 
                                snapshot.rawData.reviews.data.reviews.length || 0 : 
                                '❌'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ArticleIcon color={snapshot.rawData?.posts?.data?.localPosts ? "success" : "error"} fontSize="small" />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                              {snapshot.rawData?.posts?.data?.localPosts ? 
                                snapshot.rawData.posts.data.localPosts.length || 0 : 
                                '❌'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <QuestionAnswerIcon color={snapshot.rawData?.qa?.data?.questions ? "success" : "error"} fontSize="small" />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                              {snapshot.rawData?.qa?.data?.questions ? 
                                snapshot.rawData.qa.data.questions.length || 0 : 
                                '❌'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhotoCameraIcon color={snapshot.rawData?.photos?.data?.mediaItems ? "success" : "error"} fontSize="small" />
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                              {snapshot.rawData?.photos?.data?.mediaItems ? 
                                snapshot.rawData.photos.data.mediaItems.length || 0 : 
                                '❌'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewSnapshotDetails(snapshot)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Snapshot Details Dialog */}
      <Dialog
        open={snapshotDetailsOpen}
        onClose={() => setSnapshotDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          className: 'modern-dialog'
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            Snapshot Details - {selectedSnapshot && formatDate(selectedSnapshot.captureDate)}
          </Typography>
          <IconButton onClick={() => setSnapshotDetailsOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedSnapshot && (
            <Box>
              {/* API Status */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  🔌 API Status
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StarIcon color={selectedSnapshot.rawData?.reviews?.data?.reviews ? "success" : "error"} />
                      <Typography variant="body2">
                        <strong>Reviews:</strong> {selectedSnapshot.rawData?.reviews?.data?.reviews ? 
                          `✅ ${selectedSnapshot.rawData.reviews.data.reviews.length || 0} reviews` : 
                          '❌ Failed'} ({selectedSnapshot.rawData?.reviews?.status || 'N/A'})
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ArticleIcon color={selectedSnapshot.rawData?.posts?.data?.localPosts ? "success" : "error"} />
                      <Typography variant="body2">
                        <strong>Posts:</strong> {selectedSnapshot.rawData?.posts?.data?.localPosts ? 
                          `✅ ${selectedSnapshot.rawData.posts.data.localPosts.length || 0} posts` : 
                          '❌ Failed'} ({selectedSnapshot.rawData?.posts?.status || 'N/A'})
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <QuestionAnswerIcon color={selectedSnapshot.rawData?.qa?.data?.questions ? "success" : "error"} />
                      <Typography variant="body2">
                        <strong>Q&A:</strong> {selectedSnapshot.rawData?.qa?.data?.questions ? 
                          `✅ ${selectedSnapshot.rawData.qa.data.questions.length || 0} questions` : 
                          '❌ Failed'} ({selectedSnapshot.rawData?.qa?.status || 'N/A'})
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhotoCameraIcon color={selectedSnapshot.rawData?.photos?.data?.mediaItems ? "success" : "error"} />
                      <Typography variant="body2">
                        <strong>Photos:</strong> {selectedSnapshot.rawData?.photos?.data?.mediaItems ? 
                          `✅ ${selectedSnapshot.rawData.photos.data.mediaItems.length || 0} photos` : 
                          '❌ Failed'} ({selectedSnapshot.rawData?.photos?.status || 'N/A'})
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Data Viewer Tabs */}
              <Box sx={{ mt: 4 }}>
                <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
                  <Tab label="Business Info" icon={<BusinessIcon />} iconPosition="start" />
                  <Tab label="Reviews" icon={<StarIcon />} iconPosition="start" />
                  <Tab label="Posts" icon={<ArticleIcon />} iconPosition="start" />
                  <Tab label="Q&A" icon={<QAIcon />} iconPosition="start" />
                  <Tab label="Photos" icon={<PhotoCameraIcon />} iconPosition="start" />
                  <Tab label="Raw Data" icon={<CodeIcon />} iconPosition="start" />
                  <Tab label="Raw Business" icon={<BusinessIcon />} iconPosition="start" />
                </Tabs>

                <Box sx={{ mt: 2 }}>
                  {/* Business Info Tab */}
                  {selectedTab === 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        🏢 Business Information
                      </Typography>
                      
                      {selectedSnapshot.rawData?.listing ? (
                        selectedSnapshot.rawData.listing.data ? (
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" gutterBottom>
                                Basic Information
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Name:</strong> {selectedSnapshot.rawData.listing.data?.title || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Store Code:</strong> {selectedSnapshot.rawData.listing.data?.storeCode || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Website:</strong> {selectedSnapshot.rawData.listing.data?.websiteUri || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Primary Category:</strong> {selectedSnapshot.rawData.listing.data?.categories?.primaryCategory?.displayName || 'N/A'}
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle1" gutterBottom>
                                Address Information
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>Address:</strong> {selectedSnapshot.rawData.listing.data?.storefrontAddress?.addressLines?.join(', ') || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>City:</strong> {selectedSnapshot.rawData.listing.data?.storefrontAddress?.locality || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>State:</strong> {selectedSnapshot.rawData.listing.data?.storefrontAddress?.administrativeArea || 'N/A'}
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                <strong>ZIP:</strong> {selectedSnapshot.rawData.listing.data?.storefrontAddress?.postalCode || 'N/A'}
                              </Typography>
                            </Grid>
                            
                            {/* Additional Categories */}
                            {selectedSnapshot.rawData.listing.data?.categories?.additionalCategories && 
                             selectedSnapshot.rawData.listing.data.categories.additionalCategories.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Additional Categories
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {selectedSnapshot.rawData.listing.data.categories.additionalCategories.map((cat, index) => (
                                    <Chip
                                      key={index}
                                      label={cat.displayName}
                                      size="small"
                                      variant="outlined"
                                      className="modern-chip"
                                    />
                                  ))}
                                </Box>
                              </Grid>
                            )}
                            
                            {/* Business Hours */}
                            {selectedSnapshot.rawData.listing.data?.regularHours?.periods && 
                             selectedSnapshot.rawData.listing.data.regularHours.periods.length > 0 && (
                              <Grid item xs={12}>
                                <Typography variant="subtitle1" gutterBottom>
                                  Business Hours
                                </Typography>
                                
                                {/* Debug: Log business hours data */}
                                {console.log('🔍 Business Hours Data:', selectedSnapshot.rawData.listing.data.regularHours)}
                                {console.log('🔍 Business Hours Periods:', selectedSnapshot.rawData.listing.data.regularHours.periods)}
                                
                                <Grid container spacing={2}>
                                  {selectedSnapshot.rawData.listing.data.regularHours.periods.map((period, index) => (
                                    <Grid item xs={12} sm={6} key={index}>
                                      <Typography variant="body2" color="textSecondary">
                                        <strong>{typeof period.openDay === 'string' ? period.openDay : JSON.stringify(period.openDay)}:</strong> {typeof period.openTime === 'string' ? period.openTime : JSON.stringify(period.openTime)} - {typeof period.closeTime === 'string' ? period.closeTime : JSON.stringify(period.closeTime)}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Grid>
                            )}
                          </Grid>
                        ) : (
                          <Alert severity="error">
                            Business information API call failed. Status: {selectedSnapshot.rawData.listing.status || 'Unknown'} 
                            {selectedSnapshot.rawData.listing.error && ` - ${selectedSnapshot.rawData.listing.error}`}
                          </Alert>
                        )
                      ) : (
                        <Alert severity="warning">
                          No business listing data available. This might be because:
                          <ul style={{ marginTop: 8, marginBottom: 0 }}>
                            <li>The listing API call wasn't made during snapshot capture</li>
                            <li>The business doesn't have listing information</li>
                            <li>There was an error during the API call</li>
                          </ul>
                        </Alert>
                      )}
                      
                      {/* Debug Information */}
                      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          🔍 Debug Information
                        </Typography>
                                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '12px' }}>
                      <strong>Has listing data:</strong> {selectedSnapshot.rawData?.listing ? 'Yes' : 'No'}<br/>
                      <strong>Listing status:</strong> {selectedSnapshot.rawData?.listing?.status || 'N/A'}<br/>
                      <strong>Has listing.data:</strong> {selectedSnapshot.rawData?.listing?.data ? 'Yes' : 'No'}<br/>
                      <strong>Available data keys:</strong> {selectedSnapshot.rawData ? Object.keys(selectedSnapshot.rawData).join(', ') : 'None'}<br/>
                      <strong>Listing data keys:</strong> {selectedSnapshot.rawData?.listing?.data ? Object.keys(selectedSnapshot.rawData.listing.data).join(', ') : 'None'}<br/>
                      <strong>Listing data type:</strong> {selectedSnapshot.rawData?.listing?.data ? typeof selectedSnapshot.rawData.listing.data : 'N/A'}<br/>
                      <strong>Raw listing data:</strong> {selectedSnapshot.rawData?.listing ? JSON.stringify(selectedSnapshot.rawData.listing).substring(0, 200) + '...' : 'None'}
                    </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Reviews Tab */}
                  {selectedTab === 1 && (
                    <Box>
                      {selectedSnapshot.rawData?.reviews ? (
                        <LazyDataViewer
                          snapshotId={selectedSnapshot._id}
                          dataType="reviews"
                          title="⭐ Reviews"
                          icon={StarIcon}
                        />
                      ) : (
                        <Alert severity="error">
                          Reviews data not available. Status: {selectedSnapshot.rawData?.reviews?.status || 'Unknown'}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Posts Tab */}
                  {selectedTab === 2 && (
                    <Box>
                      {selectedSnapshot.rawData?.posts ? (
                        <LazyDataViewer
                          snapshotId={selectedSnapshot._id}
                          dataType="posts"
                          title="📝 Posts"
                          icon={ArticleIcon}
                        />
                      ) : (
                        <Alert severity="error">
                          Posts data not available. Status: {selectedSnapshot.rawData?.posts?.status || 'Unknown'}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Q&A Tab */}
                  {selectedTab === 3 && (
                    <Box>
                      {selectedSnapshot.rawData?.qa ? (
                        <LazyDataViewer
                          snapshotId={selectedSnapshot._id}
                          dataType="qa"
                          title="❓ Q&A"
                          icon={QAIcon}
                        />
                      ) : (
                        <Alert severity="error">
                          Q&A data not available. Status: {selectedSnapshot.rawData?.qa?.status || 'Unknown'}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Photos Tab */}
                  {selectedTab === 4 && (
                    <Box>
                      {selectedSnapshot.rawData?.photos ? (
                        <LazyDataViewer
                          snapshotId={selectedSnapshot._id}
                          dataType="photos"
                          title="📸 Photos"
                          icon={PhotoCameraIcon}
                        />
                      ) : (
                        <Alert severity="error">
                          Photos data not available. Status: {selectedSnapshot.rawData?.photos?.status || 'Unknown'}
                        </Alert>
                      )}
                    </Box>
                  )}

                  {/* Raw JSON Tab */}
                  {selectedTab === 5 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        🔧 Raw JSON Data
                      </Typography>
                      <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                        <pre style={{ 
                          fontSize: '12px', 
                          lineHeight: '1.4',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}>
                          {JSON.stringify(selectedSnapshot.rawData, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  )}

                  {/* Raw Business Data Tab */}
                  {selectedTab === 6 && (
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        🏢 Raw Business Data
                      </Typography>
                      {selectedSnapshot.rawData?.listing ? (
                        <Paper sx={{ p: 2, maxHeight: 400, overflow: 'auto' }}>
                          <pre style={{ 
                            fontSize: '12px', 
                            lineHeight: '1.4',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}>
                            {JSON.stringify(selectedSnapshot.rawData.listing, null, 2)}
                          </pre>
                        </Paper>
                      ) : (
                        <Alert severity="error">
                          Business listing data not available. Status: {selectedSnapshot.rawData?.listing?.status || 'Unknown'}
                        </Alert>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnapshotDetailsOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProfileDetails;